import {
  IoStorefrontOutline,
  IoBusinessOutline,
  IoBagHandleOutline,
  IoMedkitOutline,
} from "react-icons/io5";

/** Distinctive colors + icons per store — matches store branding intent. */
const STORE_AVATARS = {
  jannelli: {
    bg: "linear-gradient(135deg, #E2001A 0%, #B00016 100%)",
    fg: "#FFFFFF",
    ring: "rgba(226,0,26,0.25)",
    Icon: IoStorefrontOutline,
    initial: "J",
  },
  mazzini: {
    bg: "linear-gradient(135deg, #1E3A8A 0%, #172554 100%)",
    fg: "#FFFFFF",
    ring: "rgba(30,58,138,0.25)",
    Icon: IoBusinessOutline,
    initial: "M",
  },
  materdomini: {
    bg: "linear-gradient(135deg, #047857 0%, #064E3B 100%)",
    fg: "#FFFFFF",
    ring: "rgba(4,120,87,0.25)",
    Icon: IoBagHandleOutline,
    initial: "D",
  },
  parafarmacia: {
    bg: "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)",
    fg: "#FFFFFF",
    ring: "rgba(245,158,11,0.28)",
    Icon: IoMedkitOutline,
    initial: "P",
  },
};

const FALLBACK = {
  bg: "linear-gradient(135deg, #6B7280 0%, #374151 100%)",
  fg: "#FFFFFF",
  ring: "rgba(107,114,128,0.25)",
  Icon: IoStorefrontOutline,
  initial: "?",
};

export function getStoreAvatar(storeId) {
  return STORE_AVATARS[storeId] || FALLBACK;
}

/**
 * Circular colored avatar for a single store.
 * size: total diameter (default 44)
 * If imageUrl is provided → shows the real store image; otherwise falls back to a
 * distinctive colored gradient + icon (or initial).
 */
export function StoreAvatar({ storeId, size = 44, showInitial = false, imageUrl = "" }) {
  const a = getStoreAvatar(storeId);
  const Icon = a.Icon;
  if (imageUrl) {
    return (
      <span
        className="tf-store-avatar"
        data-testid={`store-avatar-${storeId}`}
        style={{ width: size, height: size, boxShadow: `0 0 0 3px ${a.ring}`, overflow: "hidden" }}
        aria-label={storeId}
      >
        <img
          src={imageUrl}
          alt={storeId}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
        />
      </span>
    );
  }
  return (
    <span
      className="tf-store-avatar"
      data-testid={`store-avatar-${storeId}`}
      style={{
        width: size,
        height: size,
        background: a.bg,
        boxShadow: `0 0 0 3px ${a.ring}`,
        color: a.fg,
      }}
      aria-label={storeId}
    >
      {showInitial ? (
        <span style={{ fontWeight: 600, fontSize: size * 0.42, letterSpacing: -0.5 }}>{a.initial}</span>
      ) : (
        <Icon size={Math.round(size * 0.5)} />
      )}
    </span>
  );
}

/**
 * Combined two-store avatar used for Titolare view where the conversation is
 * between two specific stores.  Renders overlapping circles.
 * imageUrls: optional map { [storeId]: url } to use real images per side.
 */
export function StorePairAvatar({ storeA, storeB, size = 44, imageUrls = {} }) {
  const a = getStoreAvatar(storeA);
  const b = getStoreAvatar(storeB);
  const AIcon = a.Icon;
  const BIcon = b.Icon;
  const inner = Math.round(size * 0.72);
  const imgA = imageUrls[storeA];
  const imgB = imageUrls[storeB];
  const circle = (i, storeId, style, fallbackBg, fallbackColor, FallbackIcon) => (
    <span
      key={i}
      style={{ ...style, borderRadius: "50%", overflow: "hidden", boxShadow: "0 0 0 2px var(--tf-surface)", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      {imageUrls[storeId] ? (
        <img src={imageUrls[storeId]} alt={storeId} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ width: "100%", height: "100%", background: fallbackBg, color: fallbackColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FallbackIcon size={Math.round(inner * 0.5)} />
        </span>
      )}
    </span>
  );
  return (
    <span
      className="tf-store-pair-avatar"
      data-testid={`store-pair-avatar-${storeA}-${storeB}`}
      style={{ width: size + Math.round(inner * 0.45), height: size, position: "relative", display: "inline-block" }}
      aria-label={`${storeA} · ${storeB}`}
    >
      {circle("a", storeA, { position: "absolute", left: 0, top: (size - inner) / 2, width: inner, height: inner }, a.bg, a.fg, AIcon)}
      {circle("b", storeB, { position: "absolute", right: 0, top: (size - inner) / 2, width: inner, height: inner }, b.bg, b.fg, BIcon)}
      {/* Prevent lint warning about unused imgA/imgB variables */}
      <span style={{ display: "none" }}>{imgA ? "" : ""}{imgB ? "" : ""}</span>
    </span>
  );
}
